import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { toast } from "react-toastify";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  loadAllBonuses,
  assignBonusToEmployee,
  loadEmployeeAssignedBonus,
  disableBonusFromEmployee,
  deleteBonusFromEmployee,
  updateAssignedBonus,
} from "../../../../../slice/thunks";
import { parseDate } from "../../../../../components/Common/Utilities";

const useBonusData = () => {
  const { t } = useTranslation();
  const { id: employeeId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const employeeFromState = location.state?.employee;

  // ===== SELECTOR =====
  const selectBonusData = useMemo(
    () =>
      createSelector(
        (state) => state.Payroll,
        (payroll) => ({
          bonusList: payroll.bonusList || [],
          assignedBonusesFromStore: payroll.assignedBonuses || [],
          error: payroll.error,
          isUpdating: payroll.isUpdating || false, // Ajout de l'état de mise à jour
        })
      ),
    []
  );

  const {
    bonusList,
    assignedBonusesFromStore,
    error,
    isUpdating,
  } = useSelector(selectBonusData);

  // ===== STATES =====
  const [employee, setEmployee] = useState(employeeFromState || null);
  const [assignedBonuses, setAssignedBonuses] = useState([]);
  const [selectedBonus, setSelectedBonusState] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isTaxable, setIsTaxable] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(!employeeFromState);
  const [isLoadingBonuses, setIsLoadingBonuses] = useState(true);
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [backendError, setBackendError] = useState("");

  // Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bonusToDelete, setBonusToDelete] = useState(null);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [bonusToDisable, setBonusToDisable] = useState(null);
  const [currentBonus, setCurrentBonus] = useState(null);

  // États pour la modification
  const [showEditModal, setShowEditModal] = useState(false);
  const [bonusToEdit, setBonusToEdit] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editIsTaxable, setEditIsTaxable] = useState(false);

  // ===== UTILITAIRES =====
  const normalize = (s) => (s ?? "").toString().trim().toLowerCase();

  // Vérif par nom
  const isAssignedByName = useCallback(
    (bonusName) =>
      assignedBonuses.some(
        (ab) => normalize(ab?.bonus?.name) === normalize(bonusName)
      ),
    [assignedBonuses]
  );

  // Vérif par id → nom
  const isAssignedById = useCallback(
    (bonusId) => {
      const b = bonusList?.find((x) => x.id === bonusId);
      return b ? isAssignedByName(b.name) : false;
    },
    [bonusList, isAssignedByName]
  );

  const handleBonusSelection = useCallback(
    (bonusId) => {
      if (bonusId && isAssignedById(bonusId)) {
        toast.error(t("bonus_already_assigned"));
        return;
      }
      setSelectedBonusState(bonusId);
    },
    [isAssignedById, t]
  );

  // ===== MODAL HANDLERS =====
  const openDeleteModal = useCallback((bonus) => {
    setBonusToDelete(bonus);
    setShowDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (!isDeleting) {
      setShowDeleteModal(false);
      setBonusToDelete(null);
      setBackendError("");
    }
  }, [isDeleting]);

  const openDisableModal = useCallback((bonus) => {
    setBonusToDisable(bonus);
    setShowDisableModal(true);
  }, []);

  const closeDisableModal = useCallback(() => {
    if (!isDisabling) {
      setShowDisableModal(false);
      setBonusToDisable(null);
    }
  }, [isDisabling]);

  // Nouveaux handlers pour la modification
  const openEditModal = useCallback((bonus) => {
    setBonusToEdit(bonus);
    setEditAmount(bonus.amount?.toString() || "");
    setEditStartDate(bonus.startDate || "");
    setEditEndDate(bonus.endDate || "");
    setEditIsTaxable(bonus.isTaxable || false);
    setShowEditModal(true);
  }, []);

  const closeEditModal = useCallback(() => {
    if (!isUpdating) {
      setShowEditModal(false);
      setBonusToEdit(null);
      setEditAmount("");
      setEditStartDate("");
      setEditEndDate("");
      setEditIsTaxable(false);
      setBackendError("");
    }
  }, [isUpdating]);

  const clearForm = useCallback(() => {
    setSelectedBonusState("");
    setCustomAmount("");
    setStartDate("");
    setEndDate("");
    setIsTaxable(false);
    setCurrentBonus(null);
    setBackendError("");
  }, []);

  // ===== FETCH INITIAL =====
  useEffect(() => {
    if (!employeeId) return;

    const fetchData = async () => {
      setIsLoading(true);
      setBackendError("");
      try {
        await Promise.all([
          dispatch(loadAllBonuses()).unwrap(),
          dispatch(loadEmployeeAssignedBonus(employeeId)).unwrap(),
        ]);
      } catch (err) {
        const msg = err?.message || t("data_loading_error");
        setBackendError(msg);
        toast.error(msg);
      } finally {
        setIsLoading(false);
        setIsLoadingBonuses(false);
        setIsLoadingAssigned(false);
      }
    };

    fetchData();
  }, [dispatch, employeeId, t]);

  // ===== SYNC ASSIGNED BONUSES =====
  useEffect(() => {
    if (assignedBonusesFromStore && assignedBonusesFromStore.length >= 0) {
      const formatted = assignedBonusesFromStore.map((item) => ({
        id: item.id,
        amount: item.amount,
        bonus: {
          id: item.bonusId,
          name: item.bonusName || item.bonus?.name,
        },
        startDate: item.startDate,
        endDate: item.endDate,
        isTaxable: item.taxable || item.isTaxable,
      }));
      setAssignedBonuses(formatted);
    } else {
      setAssignedBonuses([]);
    }
  }, [assignedBonusesFromStore]);

  // ===== BONUS DISPONIBLES =====
  const availableBonuses = useMemo(() => {
    return bonusList?.filter((bonus) => !isAssignedByName(bonus.name)) || [];
  }, [bonusList, isAssignedByName]);

  // ===== CURRENT BONUS =====
  useEffect(() => {
    if (!selectedBonus || !customAmount || !startDate) {
      setCurrentBonus(null);
      return;
    }

    const bonus = availableBonuses.find((b) => b.id === selectedBonus);
    if (!bonus) {
      setCurrentBonus(null);
      return;
    }

    const amount = parseFloat(customAmount) || 0;

    setCurrentBonus({
      bonusId: bonus.id,
      amount,
      bonus,
      startDate,
      endDate: endDate || null,
      isTaxable,
    });
  }, [selectedBonus, customAmount, startDate, endDate, isTaxable, availableBonuses]);

  // ===== HANDLERS =====
  const handleSubmit = useCallback(async () => {
    if (!currentBonus) return;
    if (isAssignedByName(currentBonus?.bonus?.name)) {
      toast.error(t("bonus_already_assigned"));
      return;
    }

    setIsSaving(true);
    setBackendError("");
    try {
      const payload = {
        bonusId: currentBonus.bonusId,
        amount: currentBonus.amount,
        startDate: currentBonus.startDate,
        endDate: currentBonus.endDate || null,
        taxable: currentBonus.isTaxable,
      };

      await dispatch(assignBonusToEmployee({ employeeId, data: payload })).unwrap();
      await dispatch(loadEmployeeAssignedBonus(employeeId)).unwrap();
      toast.success(t("bonus_successfully_assigned"));
      clearForm();
    } catch (err) {
      const msg =
        err?.message || err?.data?.message || t("assignment_error");
      setBackendError(msg);
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }, [currentBonus, dispatch, employeeId, clearForm, isAssignedByName, t]);

  // Nouveau handler pour la mise à jour
  const handleUpdateBonus = useCallback(async () => {
    if (!bonusToEdit) return;

    setBackendError("");
    try {
      const payload = {
        amount: parseFloat(editAmount) || 0,
        startDate: editStartDate,
        endDate: editEndDate || null,
        taxable: editIsTaxable,
        bonusId: bonusToEdit.bonus.id
      };
      console.log(payload);
      await dispatch(updateAssignedBonus({
        employeeId,
        employeeBonusId: bonusToEdit.id,
        data: payload
      })).unwrap();
      console.log("bonusId", bonusToEdit.bonus.id);
      console.log("bonusAssignedId", bonusToEdit.id);

      await dispatch(loadEmployeeAssignedBonus(employeeId)).unwrap();
      toast.success(t("bonus_updated_successfully", { bonusName: bonusToEdit.bonus.name }));
      closeEditModal();
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("update_error");
      setBackendError(msg);
      toast.error(msg);
    }
  }, [bonusToEdit, editAmount, editStartDate, editEndDate, editIsTaxable, dispatch, employeeId, closeEditModal, t]);

  const handleRemoveBonus = useCallback(
    async (assignedBonus) => {
      setIsDeleting(true);
      setBackendError("");
      try {
        await dispatch(
          deleteBonusFromEmployee({
            employeeId,
            employeeBonusId: assignedBonus.id,
          })
        ).unwrap();
        await dispatch(loadEmployeeAssignedBonus(employeeId)).unwrap();
        toast.success(t("bonus_deleted_successfully", { bonusName: assignedBonus.bonus.name }));
        setShowDeleteModal(false);
        setBonusToDelete(null);
      } catch (err) {
        const msg =
          err?.message || err?.data?.message || t("deletion_error");
        setBackendError(msg);
        toast.error(msg);
      } finally {
        setIsDeleting(false);
      }
    },
    [dispatch, employeeId, t]
  );

  const handleDisableBonus = useCallback(async () => {
    if (!bonusToDisable) return;
    setIsDisabling(true);
    try {
      await dispatch(
        disableBonusFromEmployee({
          employeeId,
          employeeBonusId: bonusToDisable.id,
        })
      ).unwrap();
      await dispatch(loadEmployeeAssignedBonus(employeeId)).unwrap();
      toast.success(t("bonus_disabled_successfully", { bonusName: bonusToDisable.bonus.name }));
      setShowDisableModal(false);
      setBonusToDisable(null);
    } catch (err) {
      toast.error(err?.message || t("disable_error"));
    } finally {
      setIsDisabling(false);
    }
  }, [bonusToDisable, dispatch, employeeId, t]);

  // ===== TABLE DATA =====
  const tableData = useMemo(
    () =>
      assignedBonuses.map((ab) => ({
        ...ab,
        startDateFormatted: parseDate(ab.startDate),
        endDateFormatted:ab.endDate? parseDate(ab.endDate) : "-",
        amountDisplay: `${ab.amount?.toLocaleString()}`,
        taxableDisplay: ab.isTaxable ? t("yes") : t("no"),
      })),
    [assignedBonuses, t]
  );

  const bonusColumns = useMemo(
    () => [
      {
        header: t("bonus"),
        accessorKey: "bonus.name",
        enableColumnFilter: false,
      },
      {
        header: t("amount"),
        accessorKey: "amountDisplay",
        enableColumnFilter: false,
      },
      {
        header: t("start_date"),
        accessorKey: "startDateFormatted",
        enableColumnFilter: false,
      },
      {
        header: t("end_date"),
        accessorKey: "endDateFormatted",
        enableColumnFilter: false,
      },
      {
        header: t("taxable"),
        accessorKey: "taxableDisplay",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <span
              className={`badge ${
                value === t("yes") ? "bg-warning" : "bg-info"
              }`}
            >
              {value}
            </span>
          );
        },
      },
    ],
    [t]
  );

  // ===== CALCULATIONS =====
  const totalBonuses = useMemo(
    () => assignedBonuses.reduce((total, ab) => total + (ab.amount || 0), 0),
    [assignedBonuses]
  );

  return {
    employee,
    isLoading: isLoading || isLoadingBonuses || isLoadingAssigned,
    isSaving,
    isDeleting,
    isDisabling,
    isUpdating,
    successMessage,
    backendError,
    assignedBonuses,
    allBonuses: availableBonuses,
    totalBonuses,
    selectedBonus,
    setSelectedBonus: handleBonusSelection,
    customAmount,
    setCustomAmount,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isTaxable,
    setIsTaxable,
    currentBonus,
    handleSubmit,
    handleRemoveBonus,
    openDeleteModal,
    closeDeleteModal,
    showDeleteModal,
    bonusToDelete,
    openDisableModal,
    closeDisableModal,
    showDisableModal,
    bonusToDisable,
    handleDisableBonus,
    clearForm,
    tableData,
    bonusColumns,
    isBonusAlreadyAssigned: isAssignedByName,
    
    // Nouveaux retours pour la modification
    openEditModal,
    closeEditModal,
    showEditModal,
    bonusToEdit,
    editAmount,
    setEditAmount,
    editStartDate,
    setEditStartDate,
    editEndDate,
    setEditEndDate,
    editIsTaxable,
    setEditIsTaxable,
    handleUpdateBonus,
  };
};

export default useBonusData;